import localforage from 'localforage';
import { print, type DocumentNode } from 'graphql';
import { getMainDefinition } from '@apollo/client/utilities';
import { ensurePersistentStorage } from './persistentStorage';
import { ownerForEnqueue, sameOwner, type QueueOwner } from './queueOwner';

const QUEUE_KEY = 'rois_offline_mutation_queue';
/** Clave de esta cola en localforage: el logout la conserva (spec 2026-10-12). */
export const MUTATION_QUEUE_STORAGE_KEY = QUEUE_KEY;

export interface QueuedMutation {
  /**
   * Quién la encoló (spec 2026-10-01 § 6). Opcional: las encoladas antes de
   * esta versión no lo llevan y se envían con la sesión actual, como siempre.
   */
  owner?: QueueOwner;
  id: string;
  operationName: string;
  query: string;
  variables: Record<string, unknown>;
  timestamp: number;
  retryCount: number;
}

// Mutex lock to prevent race conditions during rapid async queue updates
let queueLock: Promise<void> = Promise.resolve();

async function withLock<T>(action: () => Promise<T>): Promise<T> {
  const nextLock = queueLock.then(() => action()).catch((err) => {
    console.error('Error in offline queue lock:', err);
    throw err;
  });
  queueLock = nextLock.then(() => { }).catch(() => { });
  return nextLock;
}

export async function getQueue(): Promise<QueuedMutation[]> {
  try {
    const data = await localforage.getItem<QueuedMutation[]>(QUEUE_KEY);
    return data || [];
  } catch (error) {
    console.error('Failed to get offline queue from localforage', error);
    return [];
  }
}

export async function addToQueue(
  mutation: Pick<QueuedMutation, 'operationName' | 'query' | 'variables'>,
  explicitOwner?: QueueOwner,
): Promise<void> {
  return withLock(async () => {
    const queue = await getQueue();

    const owner = explicitOwner ?? ownerForEnqueue();

    // Deduplicate: don't queue the same mutation + variables twice FOR THE SAME
    // OWNER. Una antigua sin sello cuenta como del usuario actual (como antes).
    const isDuplicate = queue.some(
      (m) =>
        m.operationName === mutation.operationName &&
        JSON.stringify(m.variables) === JSON.stringify(mutation.variables) &&
        (!m.owner || sameOwner(m.owner, owner))
    );
    if (isDuplicate) return;

    queue.push({
      ...mutation,
      owner,
      id: `offline_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      timestamp: Date.now(),
      retryCount: 0,
    });

    try {
      await localforage.setItem(QUEUE_KEY, queue);
      window.dispatchEvent(new CustomEvent('offline-queue-changed'));

      // Acabamos de guardar trabajo del operario que aún no ha llegado al
      // servidor. Este es el momento en que de verdad importa que el navegador
      // no pueda desalojarnos — y además es cuando más probable es que nos lo
      // conceda, porque ya nos considera "un sitio que esta persona usa"
      // (Chrome decide por heurísticas de uso, sin preguntar).
      //
      // Fire-and-forget a propósito: no metemos latencia en el camino crítico
      // del operario por una llamada que, si falla, no cambia nada.
      void ensurePersistentStorage('cola offline con trabajo pendiente');
    } catch (error) {
      console.error('Espacio insuficiente en disco para guardar la operación offline', error);
    }
  });
}

/**
 * Reescribe las variables de operaciones ya encoladas.
 *
 * Existe para un caso muy concreto: un tramo creado sin cobertura recibe un id
 * sintético, y las operaciones que lo referencian (cerrarlo, corregirlo) se
 * encolan con ESE id. Cuando el create se reproduce y el servidor asigna el id
 * real, hay que traducirlo en lo que quede en la cola o esas operaciones morirán
 * apuntando a un id inexistente. Ver `remapPendingSegmentId.ts`.
 *
 * Se escribe la cola entera bajo el mismo lock que el resto de escrituras: dos
 * actualizaciones concurrentes sobre la misma clave de localforage se pisan.
 */
export async function updateQueuedVariables(
  updates: Array<Pick<QueuedMutation, 'id' | 'variables'>>
): Promise<void> {
  if (updates.length === 0) return;
  return withLock(async () => {
    const queue = await getQueue();
    const byId = new Map(updates.map((u) => [u.id, u.variables]));
    let changed = false;
    const next = queue.map((mutation) => {
      const variables = byId.get(mutation.id);
      if (!variables) return mutation;
      changed = true;
      return { ...mutation, variables };
    });
    if (!changed) return;
    try {
      await localforage.setItem(QUEUE_KEY, next);
      window.dispatchEvent(new CustomEvent('offline-queue-changed'));
    } catch (error) {
      console.error('Failed to update queued variables', error);
    }
  });
}

export async function removeFromQueue(id: string): Promise<void> {
  return withLock(async () => {
    const queue = await getQueue();
    const filteredQueue = queue.filter((m) => m.id !== id);
    await localforage.setItem(QUEUE_KEY, filteredQueue);
    window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  });
}

export async function incrementRetryCount(id: string): Promise<void> {
  return withLock(async () => {
    const queue = await getQueue();
    const updatedQueue = queue.map((m) =>
      m.id === id ? { ...m, retryCount: m.retryCount + 1 } : m
    );
    await localforage.setItem(QUEUE_KEY, updatedQueue);
  });
}

export async function clearQueue(): Promise<void> {
  return withLock(async () => {
    await localforage.removeItem(QUEUE_KEY);
    window.dispatchEvent(new CustomEvent('offline-queue-changed'));
  });
}

export async function getQueueCount(): Promise<number> {
  const queue = await getQueue();
  return queue.length;
}

/**
 * Encola una mutation SIN pasar por el link (sondeo previo de los asistentes,
 * 2026-09-08). Serializa exactamente igual que `OfflineQueueLink.queueMutation`
 * —`operationName` de la definición principal + `print(document)`— para que la
 * deduplicación por variables y el replay vean la misma entrada.
 */
export async function enqueueMutation(
  document: DocumentNode,
  variables: Record<string, unknown>,
  owner?: QueueOwner,
): Promise<void> {
  const definition = getMainDefinition(document);
  const operationName =
    definition.kind === 'OperationDefinition' ? definition.name?.value ?? '' : '';
  await addToQueue({ operationName, query: print(document), variables }, owner);
}

/**
 * Sella con `owner` las operaciones que no tienen dueño (las encoladas antes de
 * la #331) y devuelve cuántas selló. Lo llama el logout ANTES de soltar la
 * sesión: la cola ya no se borra al salir (spec 2026-10-12), y una operación sin
 * dueño la enviaría el siguiente operario con su token y a su tenant.
 */
export async function sealUnownedOperations(owner: QueueOwner | undefined): Promise<number> {
  if (!owner) return 0;
  return withLock(async () => {
    const queue = await getQueue();
    let sealed = 0;
    const updated = queue.map((op): QueuedMutation => {
      if (op.owner) return op;
      sealed += 1;
      return { ...op, owner };
    });
    if (sealed > 0) await localforage.setItem(QUEUE_KEY, updated);
    return sealed;
  });
}
